import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruck, faEuroSign } from '@fortawesome/free-solid-svg-icons';
import { ChangeIndicator } from '../common/ChangeIndicator';
import { BackendChangeIndicator } from '../common/BackendChangeIndicator';

const ArticleDetailVertrieb: React.FC<{
  data: any;
  mode: 'view' | 'edit';
  onChange: (field: string, value: any) => void;
  isSupplierMode?: boolean;
  originalData?: any;
  articleNumber?: string;
}> = ({ data, mode, onChange, isSupplierMode = false, originalData, articleNumber }) => {
  // **UTILITY FUNCTIONS FOR CHANGE DETECTION**
  const getFieldValue = (obj: any, path: string) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const isFieldChanged = (fieldPath: string): boolean => {
    if (!originalData || !data) return false;
    
    // Use submissionSnapshot as baseline if available, otherwise use originalData
    const baselineData = data?.submissionSnapshot || originalData;
    const baselineValue = getFieldValue(baselineData, fieldPath);
    const currentValue = getFieldValue(data, fieldPath);
    
    // Handle null/undefined cases
    if (!baselineValue && !currentValue) return false;
    
    // For arrays, do deep comparison
    if (Array.isArray(baselineValue) && Array.isArray(currentValue)) {
      if (baselineValue.length !== currentValue.length) return true;
      return baselineValue.some((item, index) => 
        JSON.stringify(item) !== JSON.stringify(currentValue[index])
      );
    }
    
    // For objects, do deep comparison
    if (typeof baselineValue === 'object' && typeof currentValue === 'object') {
      return JSON.stringify(baselineValue) !== JSON.stringify(currentValue);
    }
    
    // For primitive values - ANY difference is a change
    return String(baselineValue) !== String(currentValue);
  };

  const isFieldNew = (fieldPath: string): boolean => {
    if (!originalData || !data) return false;
    
    // IMPORTANT: If a submissionSnapshot exists, the field can NEVER be "new" - only "changed"
    // submissionSnapshot means the supplier has already submitted data before
    const hasSubmissionSnapshot = data?.submissionSnapshot;
    if (hasSubmissionSnapshot) {
      return false; // Never "new" if already submitted before
    }
    
    // Only check if truly new (first time filling) when no snapshot exists
    const baselineValue = getFieldValue(originalData, fieldPath);
    const currentValue = getFieldValue(data, fieldPath);
    
    const baselineEmpty = !baselineValue || baselineValue === '' || baselineValue === null;
    const currentHasValue = currentValue && currentValue !== '' && currentValue !== null;
    
    // Field is "new" (green) only if it's the very first time being filled
    return baselineEmpty && currentHasValue;
  };

  const renderFieldWithHistory = (
    fieldName: string, 
    fieldPath: string,
    content: React.ReactNode
  ) => {
    // Change tracking only for Admin (not Supplier mode)
    if (isSupplierMode || !originalData || !articleNumber) {
      return content;
    }
    
    const hasChanges = isFieldChanged(fieldPath);
    const isNew = isFieldNew(fieldPath);
    
    return (
      <ChangeIndicator
        fieldName={fieldName}
        fieldPath={fieldPath}
        hasChanges={hasChanges}
        isNew={isNew}
        articleNumber={articleNumber}
      >
        {content}
      </ChangeIndicator>
    );
  };
  return (
    <div className="space-y-6">
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            {/* Test mit BackendChangeIndicator */}
            <BackendChangeIndicator
              fieldName="VK-Preis"
              fieldPath="salesPrice"
              articleNumber={articleNumber}
              isSupplierMode={isSupplierMode}
            >
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FontAwesomeIcon icon={faEuroSign} className="mr-2 text-green-600" />
                Einkaufs-Preis
              </label>
              {mode === 'edit' ? (
                <input
                  type="number"
                  step="0.01"
                  value={data?.salesPrice || ''}
                  onChange={(e) => onChange('salesPrice', parseFloat(e.target.value) || 0)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="0.00"
                />
              ) : (
                <p className="text-gray-900">
                  {data?.salesPrice ? `€ ${data.salesPrice.toFixed(2)}` : 'N/A'}
                </p>
              )}
            </BackendChangeIndicator>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetailVertrieb;
